import { useCallback, useReducer } from 'react';

import { getRandomCardBack, TAROT_DECK } from '@/data/cards';
import { type SpreadKey, SPREADS } from '@/data/spreads';
import type { DrawnEntry, ShuffledEntry } from '@/types/reading';
import { shuffle } from '@/utils/shuffle';

export type { DrawnEntry, ShuffledEntry } from '@/types/reading';

interface ReadingState {
  spread: SpreadKey;
  shuffled: ShuffledEntry[];
  drawn: DrawnEntry[];
  showResult: boolean;
  cardBack: string | null;
}

type ReadingAction =
  | { type: 'SET_SPREAD'; spread: SpreadKey }
  | { type: 'SHUFFLE'; shuffled: ShuffledEntry[]; cardBack: string }
  | { type: 'TOGGLE_SELECT'; shuffleIndex: number }
  | { type: 'COMPLETE' }
  | { type: 'RESET' };

const EMPTY_ROUND: Omit<ReadingState, 'spread'> = {
  shuffled: [],
  drawn: [],
  showResult: false,
  cardBack: null,
};

function countSelected(shuffled: readonly ShuffledEntry[]): number {
  return shuffled.reduce((n, entry) => (entry.selected ? n + 1 : n), 0);
}

function readingReducer(state: ReadingState, action: ReadingAction): ReadingState {
  switch (action.type) {
    case 'SET_SPREAD':
      return { ...EMPTY_ROUND, spread: action.spread };

    case 'SHUFFLE':
      return {
        ...EMPTY_ROUND,
        spread: state.spread,
        shuffled: action.shuffled,
        cardBack: action.cardBack,
      };

    case 'TOGGLE_SELECT': {
      const target = state.shuffled[action.shuffleIndex];
      if (!target) return state;
      // Block selecting more than the spread requires; allow deselecting freely.
      if (!target.selected && countSelected(state.shuffled) >= SPREADS[state.spread].size) {
        return state;
      }
      return {
        ...state,
        shuffled: state.shuffled.map((entry, idx) =>
          idx === action.shuffleIndex ? { ...entry, selected: !entry.selected } : entry,
        ),
      };
    }

    case 'COMPLETE': {
      const { size, positions } = SPREADS[state.spread];
      if (countSelected(state.shuffled) !== size) return state;
      const drawn: DrawnEntry[] = state.shuffled
        .filter((entry) => entry.selected)
        .map((entry, idx) => ({
          card: entry.card,
          isReversed: entry.isReversed,
          position: positions[idx],
        }));
      return { ...state, drawn, showResult: true };
    }

    case 'RESET':
      return { ...EMPTY_ROUND, spread: state.spread };
  }
}

export interface UseReadingOptions {
  /** Random source in [0, 1). Defaults to `Math.random`; inject for deterministic tests. */
  random?: () => number;
}

export interface UseReadingResult {
  spread: SpreadKey;
  setSpread: (key: SpreadKey) => void;
  shuffled: ShuffledEntry[];
  drawn: DrawnEntry[];
  showResult: boolean;
  cardBack: string | null;
  requiredCards: number;
  selectedCount: number;
  positions: readonly string[];
  shuffleDeck: () => void;
  toggleSelect: (shuffleIndex: number) => void;
  completeReading: () => void;
  reset: () => void;
}

export function useReading(
  initialSpread: SpreadKey,
  { random = Math.random }: UseReadingOptions = {},
): UseReadingResult {
  const [state, dispatch] = useReducer(readingReducer, { ...EMPTY_ROUND, spread: initialSpread });

  const { size: requiredCards, positions } = SPREADS[state.spread];
  const selectedCount = countSelected(state.shuffled);

  const setSpread = useCallback(
    (spread: SpreadKey) => dispatch({ type: 'SET_SPREAD', spread }),
    [],
  );

  const shuffleDeck = useCallback(() => {
    const shuffled: ShuffledEntry[] = shuffle(TAROT_DECK, random).map((card, index) => ({
      card,
      shuffleIndex: index,
      isReversed: random() < 0.5,
      selected: false,
    }));
    dispatch({ type: 'SHUFFLE', shuffled, cardBack: getRandomCardBack(random) });
  }, [random]);

  const toggleSelect = useCallback(
    (shuffleIndex: number) => dispatch({ type: 'TOGGLE_SELECT', shuffleIndex }),
    [],
  );
  const completeReading = useCallback(() => dispatch({ type: 'COMPLETE' }), []);
  const reset = useCallback(() => dispatch({ type: 'RESET' }), []);

  return {
    ...state,
    requiredCards,
    selectedCount,
    positions,
    setSpread,
    shuffleDeck,
    toggleSelect,
    completeReading,
    reset,
  };
}
